import React from "react";
import {
  ButtonPdfContainerStyled,
  ButtonStyled,
  DocumentsContainter,
  EquipmentListContainter,
  InputDocumentContainer,
} from "./styles";
import { MenuItem, Typography, Stack, Button } from "@mui/material";
import StyledTextField from "../StyledTextField";
import { PictureAsPdfSharp, Close } from "@mui/icons-material";
import DocumentListAccordion from "../DocumentListAccordion";

interface DocumentsTabProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  setFieldValue: (field: string, value: any) => void;
  employees: any[];
  disabled?: boolean;
  currentCustomer?: any;
  onDeleteDocument: (id: number) => void;
  isEditing?: boolean;
  onCancelEdit?: () => void;
  setEditingDocId?: (id: number | null) => void;
}

const DocumentsTab = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  employees,
  disabled = false,
  currentCustomer,
  onDeleteDocument,
  isEditing,
  onCancelEdit,
  setEditingDocId,
}: DocumentsTabProps) => {

  const getLimitedFileName = (name: string, maxLength = 15) => {
    if (!name) return "Anexar PDF";
    if (name.length <= maxLength) return name;
    const extIndex = name.lastIndexOf(".");
    const ext = extIndex !== -1 ? name.substring(extIndex) : "";
    const base = name.substring(0, maxLength - ext.length - 3);
    return base + "..." + ext;
  };

  const handleEditInternal = (doc: any) => {
    if (setEditingDocId) setEditingDocId(doc.id);
    setFieldValue("documentName", doc.name || "");
    setFieldValue("documentStatus", doc.status || "");

    const rawEmployeeId =
      doc.employee_id ||
      doc.employee?.id ||
      (doc.employees && doc.employees[0]?.id);

    setFieldValue("employee", rawEmployeeId ? Number(rawEmployeeId) : "");
    if (doc.expiration_date) {
      setFieldValue("documentExpiration", doc.expiration_date.split("T")[0]);
    } else {
      setFieldValue("documentExpiration", "");
    }
    setFieldValue("documentFile", null);
  };

  return (
    <DocumentsContainter>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
        mb={1}
      >
        <Typography variant="h6">
          {isEditing ? "Editar Documento" : "Novo Documento"}
        </Typography>
        {isEditing && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Close />}
            onClick={onCancelEdit}
          >
            Cancelar Edição
          </Button>
        )}
      </Stack>

      <InputDocumentContainer>
        <StyledTextField
          label="Nome do documento"
          name="documentName"
          value={values.documentName || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.documentName && Boolean(errors.documentName)}
          helperText={touched.documentName && (errors.documentName as string)}
          fullWidth
        />

        <StyledTextField
          select
          label="Funcionário"
          name="employee"
          value={values.employee || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.employee && Boolean(errors.employee)}
          helperText={touched.employee && (errors.employee as string)}
          fullWidth
        >
          {employees
            .sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1))
            .map((emp) => (
              <MenuItem
                key={emp.id}
                value={emp.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: emp.active ? "inherit" : "error.main",
                  textTransform: 'uppercase',
                  fontSize: 12,
                }}
              >
                {emp.name}
                <Typography
                  variant="caption"
                  sx={{
                    ml: 2,
                    fontWeight: "bold",
                    color: emp.active ? "#2e7d32" : "#d32f2f",
                    textTransform: 'capitalize',
                    fontSize: 10
                  }}
                >
                  {emp.active ? "Ativo" : "Inativo"}
                </Typography>
              </MenuItem>
            ))}
        </StyledTextField>

        <StyledTextField
          label="Data de Vencimento"
          name="documentExpiration"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={values.documentExpiration || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.documentExpiration && Boolean(errors.documentExpiration)}
          helperText={touched.documentExpiration && (errors.documentExpiration as string)}
          fullWidth
        />

        <StyledTextField
          select
          label="Status"
          name="documentStatus"
          value={values.documentStatus || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.documentStatus && Boolean(errors.documentStatus)}
          helperText={touched.documentStatus && (errors.documentStatus as string)}
          sx={{
            width: "100%",
            maxWidth: "150px",
            "& .MuiOutlinedInput-root": {
              overflow: "visible",
            },
            "& .MuiOutlinedInput-input": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              height: "auto",
            },
          }}
        >
          <MenuItem value="Vencido">Vencido</MenuItem>
          <MenuItem value="Renovado">Renovado</MenuItem>
          <MenuItem value="Ativo">A vencer</MenuItem>
        </StyledTextField>


        <input
          type="file"
          id="doc-upload"
          style={{ display: "none" }}
          accept="application/pdf"
          disabled={disabled}
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            setFieldValue("documentFile", file || null);
          }}
        />

        <ButtonPdfContainerStyled>
          <ButtonStyled
            type="button"
            variant="contained"
            startIcon={<PictureAsPdfSharp />}
            onClick={() => document.getElementById("doc-upload")?.click()}
            disabled={disabled}
          >
            {values.documentFile
              ? getLimitedFileName(values.documentFile.name)
              : "Anexar PDF"
            }
          </ButtonStyled>


          <Typography variant="caption" className="error-text">
            {errors.documentFile as string}
          </Typography>

        </ButtonPdfContainerStyled>
      </InputDocumentContainer>

      <EquipmentListContainter>
        {currentCustomer?.documents?.length > 0 && (
          <DocumentListAccordion
            documents={currentCustomer.documents}
            onDelete={onDeleteDocument}
            onEdit={handleEditInternal}
          />
        )}
      </EquipmentListContainter>
    </DocumentsContainter>
  );
};

export default DocumentsTab;